/**
 * Milestone Management - Admin UI
 * Create, edit, and manage milestone definitions
 * Trust Marketplace - Configurable Milestone System
 */

import React, { useState, useEffect } from 'react';
import { Card } from '../ui/card';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../ui/dialog';
import { LoadingSpinner } from '../ui/LoadingSpinner';
import { getWeaveinApiUrl } from '@/src/config';
import { useAppTranslation } from '@/contexts/TranslationContext';
import { authFetch } from '@/lib/authFetch';
import {
  Plus,
  Edit,
  Trash2,
  CheckCircle,
  AlertCircle,
  Target,
  DollarSign,
  Award,
  RefreshCw,
  Search
} from 'lucide-react';
import { useTabbedChrome } from '@/components/weavein';

interface Milestone {
  id: number;
  activity_type: string;
  milestone_name: string;
  milestone_description: string | null;
  points_awarded: number;
  monetary_value: number;
  is_repeatable: boolean;
  related_entity_type: string | null;
  is_active: boolean;
  created_at: string;
  updated_at: string;
  times_earned?: number;
  total_points_awarded?: number;
  total_monetary_awarded?: number;
}

interface MilestoneFormData {
  activity_type: string;
  milestone_name: string;
  milestone_description: string;
  points_awarded: number;
  monetary_value: number;
  is_repeatable: boolean;
  related_entity_type: string;
  is_active: boolean;
}

const ACTIVITY_TYPES = [
  'medicaid_enrollment',
  'health_wellness',
  'social_services',
  'transportation',
  'referral',
  'engagement',
  'housing',
  'employment',
  'education',
  'childcare',
  'other'
];

const MilestoneManagement: React.FC = () => {
  const tabbed = useTabbedChrome();
  const { t } = useAppTranslation();
  const [milestones, setMilestones] = useState<Milestone[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterType, setFilterType] = useState<string>('all');
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingMilestone, setEditingMilestone] = useState<Milestone | null>(null);

  const [formData, setFormData] = useState<MilestoneFormData>({
    activity_type: '',
    milestone_name: '',
    milestone_description: '',
    points_awarded: 0,
    monetary_value: 0,
    is_repeatable: false,
    related_entity_type: '',
    is_active: true
  });

  useEffect(() => {
    fetchMilestones();
  }, []);

  const fetchMilestones = async () => {
    try {
      setLoading(true);
      setError(null);
      const url = getWeaveinApiUrl('/api/member-activity-milestones/');

      const response = await authFetch(url);

      if (!response.ok) {
        throw new Error('Failed to fetch milestones');
      }

      const data = await response.json();
      setMilestones(data);
    } catch (err) {
      console.error('Error fetching milestones:', err);
      setError(err instanceof Error ? err.message : 'Failed to load milestones');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSuccess(null);

    // Validation
    if (!formData.activity_type || !formData.milestone_name) {
      setError('Activity type and milestone name are required');
      return;
    }

    try {
      const url = editingMilestone
        ? getWeaveinApiUrl(`/api/member-activity-milestones/${editingMilestone.id}`)
        : getWeaveinApiUrl('/api/member-activity-milestones/');

      const response = await authFetch(url, {
        method: editingMilestone ? 'PUT' : 'POST',
        jsonBody: formData
      });

      if (!response.ok) {
        const errorData = await response.json();
        console.error('Milestone save error:', errorData);

        if (response.status === 422 && Array.isArray(errorData.detail)) {
          const errorMessages = errorData.detail.map((err: any) =>
            `${err.loc?.join(' → ') || 'Field'}: ${err.msg}`
          ).join('\n');
          throw new Error(`Validation errors:\n${errorMessages}`);
        }

        throw new Error(errorData.detail || 'Failed to save milestone');
      }

      setSuccess(editingMilestone ? 'Milestone updated successfully!' : 'Milestone created successfully!');
      setIsDialogOpen(false);
      resetForm();
      fetchMilestones();

      setTimeout(() => setSuccess(null), 5000);
    } catch (err) {
      console.error('Error saving milestone:', err);
      setError(err instanceof Error ? err.message : 'Failed to save milestone');
    }
  };

  const handleEdit = (milestone: Milestone) => {
    setEditingMilestone(milestone);
    setFormData({
      activity_type: milestone.activity_type,
      milestone_name: milestone.milestone_name,
      milestone_description: milestone.milestone_description || '',
      points_awarded: milestone.points_awarded,
      monetary_value: milestone.monetary_value,
      is_repeatable: milestone.is_repeatable,
      related_entity_type: milestone.related_entity_type || '',
      is_active: milestone.is_active
    });
    setIsDialogOpen(true);
  };

  const handleDeactivate = async (milestoneId: number) => {
    if (!confirm('Are you sure you want to deactivate this milestone?')) {
      return;
    }

    try {
      const url = getWeaveinApiUrl(`/api/member-activity-milestones/${milestoneId}`);

      const response = await authFetch(url, {
        method: 'PUT',
        jsonBody: { is_active: false }
      });

      if (!response.ok) {
        throw new Error('Failed to deactivate milestone');
      }

      setSuccess('Milestone deactivated successfully!');
      fetchMilestones();
      setTimeout(() => setSuccess(null), 5000);
    } catch (err) {
      console.error('Error deactivating milestone:', err);
      setError(err instanceof Error ? err.message : 'Failed to deactivate milestone');
    }
  };

  const resetForm = () => {
    setFormData({
      activity_type: '',
      milestone_name: '',
      milestone_description: '',
      points_awarded: 0,
      monetary_value: 0,
      is_repeatable: false,
      related_entity_type: '',
      is_active: true
    });
    setEditingMilestone(null);
  };

  const filteredMilestones = milestones.filter(m => {
    const matchesSearch = m.milestone_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         m.milestone_description?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesType = filterType === 'all' || m.activity_type === filterType;
    return matchesSearch && matchesType;
  });

  const groupedMilestones = filteredMilestones.reduce((acc, milestone) => {
    if (!acc[milestone.activity_type]) {
      acc[milestone.activity_type] = [];
    }
    acc[milestone.activity_type].push(milestone);
    return acc;
  }, {} as Record<string, Milestone[]>);

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <LoadingSpinner />
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          {!tabbed && (
          <h1 className="text-3xl font-bold text-gray-900">{t('incentives.milestone_management', 'Milestone Management')}</h1>
          )}
          <p className="text-sm text-gray-600 mt-2">
            {t('incentives.create_and_manage_milestone_definitions', 'Create and manage milestone definitions for your programs')}
          </p>
        </div>
        <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
          <DialogTrigger asChild>
            <Button onClick={resetForm} className="flex items-center gap-2">
              <Plus className="w-4 h-4" />
              {t('incentives.create_new_milestone', 'Create New Milestone')}
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>
                {editingMilestone ? 'Edit Milestone' : 'Create New Milestone'}
              </DialogTitle>
            </DialogHeader>

            <form onSubmit={handleSubmit} className="space-y-4 mt-4">
              {/* Activity Type */}
              <div>
                <Label htmlFor="activity_type">{t('incentives.activity_type', 'Activity Type *')}</Label>
                <Select
                  value={formData.activity_type}
                  onValueChange={(value) => setFormData({ ...formData, activity_type: value })}
                >
                  <SelectTrigger>
                    <SelectValue placeholder={t('incentives.select_activity_type', 'Select activity type')} />
                  </SelectTrigger>
                  <SelectContent>
                    {ACTIVITY_TYPES.map((type) => (
                      <SelectItem key={type} value={type}>
                        {type.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Milestone Name */}
              <div>
                <Label htmlFor="milestone_name">{t('incentives.milestone_name', 'Milestone Name *')}</Label>
                <Input
                  id="milestone_name"
                  value={formData.milestone_name}
                  onChange={(e) => setFormData({ ...formData, milestone_name: e.target.value })}
                  placeholder="e.g., enrollment_approved"
                  required
                />
                <p className="text-xs text-gray-500 mt-1">
                  Use lowercase with underscores (e.g., enrollment_approved)
                </p>
              </div>

              {/* Description */}
              <div>
                <Label htmlFor="milestone_description">{t('incentives.description', 'Description')}</Label>
                <Textarea
                  id="milestone_description"
                  value={formData.milestone_description}
                  onChange={(e) => setFormData({ ...formData, milestone_description: e.target.value })}
                  placeholder={t('incentives.describe_what_this_milestone_represents', 'Describe what this milestone represents')}
                  rows={3}
                />
              </div>

              {/* Points and Money */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="points_awarded">{t('incentives.default_points', 'Default Points')}</Label>
                  <Input
                    id="points_awarded"
                    type="number"
                    min="0"
                    value={formData.points_awarded}
                    onChange={(e) => setFormData({ ...formData, points_awarded: parseInt(e.target.value) || 0 })}
                  />
                </div>
                <div>
                  <Label htmlFor="monetary_value">Default Monetary Value ($)</Label>
                  <Input
                    id="monetary_value"
                    type="number"
                    min="0"
                    step="0.01"
                    value={formData.monetary_value}
                    onChange={(e) => setFormData({ ...formData, monetary_value: parseFloat(e.target.value) || 0 })}
                  />
                </div>
              </div>

              {/* Repeatable */}
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="is_repeatable"
                  checked={formData.is_repeatable}
                  onChange={(e) => setFormData({ ...formData, is_repeatable: e.target.checked })}
                  className="w-4 h-4"
                />
                <Label htmlFor="is_repeatable" className="cursor-pointer">
                  Repeatable (can be earned multiple times)
                </Label>
              </div>

              {/* Related Entity Type */}
              <div>
                <Label htmlFor="related_entity_type">{t('incentives.related_entity_type', 'Related Entity Type')}</Label>
                <Input
                  id="related_entity_type"
                  value={formData.related_entity_type}
                  onChange={(e) => setFormData({ ...formData, related_entity_type: e.target.value })}
                  placeholder="e.g., medicaid_enrollment, referral"
                />
                <p className="text-xs text-gray-500 mt-1">
                  {t('incentives.optional_link_to_a_specific', 'Optional: Link to a specific entity type in your system')}
                </p>
              </div>

              {/* Active Status */}
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="is_active"
                  checked={formData.is_active}
                  onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                  className="w-4 h-4"
                />
                <Label htmlFor="is_active" className="cursor-pointer">
                  Active (available for program configuration)
                </Label>
              </div>

              {/* Error Display */}
              {error && (
                <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
                  <p className="text-sm text-red-800 whitespace-pre-wrap">{error}</p>
                </div>
              )}

              {/* Actions */}
              <div className="flex justify-end gap-2 pt-4">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => {
                    setIsDialogOpen(false);
                    resetForm();
                  }}
                >
                  {t('incentives.cancel', 'Cancel')}
                </Button>
                <Button type="submit">
                  {editingMilestone ? 'Update Milestone' : 'Create Milestone'}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Guidance Section */}
      <Card className="p-4 bg-blue-50 border-blue-200">
        <div className="flex items-start gap-3">
          <Target className="w-5 h-5 text-blue-600 mt-0.5 flex-shrink-0" />
          <div>
            <h3 className="font-semibold text-blue-900 mb-1">{t('incentives.about_milestone_types', 'About Milestone Types')}</h3>
            <p className="text-sm text-blue-800 mb-2">
              Milestone Types are reusable templates that define achievable goals for program participants.
              Once created here, these types can be configured with specific payouts when setting up programs.
            </p>
            <div className="text-sm text-blue-700">
              <strong>{t('incentives.how_to_use', 'How to use:')}</strong>
              <ul className="list-disc list-inside mt-1 space-y-1">
                <li>Create milestone types for common activities (enrollment, wellness checks, etc.)</li>
                <li>{t('incentives.set_default_point_and_monetary', 'Set default point and monetary values as templates')}</li>
                <li>{t('incentives.mark_milestones_as_repeatable_if', 'Mark milestones as repeatable if they can be earned multiple times')}</li>
                <li>{t('incentives.use_these_types_when_configuring', 'Use these types when configuring programs in "Configure Program Milestones"')}</li>
              </ul>
            </div>
          </div>
        </div>
      </Card>

      {/* Success Alert */}
      {success && (
        <Card className="p-4 bg-green-50 border-green-200">
          <div className="flex items-center gap-2 text-green-800">
            <CheckCircle className="w-5 h-5" />
            <span className="font-medium">{success}</span>
          </div>
        </Card>
      )}

      {/* Error Alert */}
      {error && (
        <Card className="p-4 bg-red-50 border-red-200">
          <div className="flex items-center gap-2 text-red-800">
            <AlertCircle className="w-5 h-5" />
            <span className="font-medium">{error}</span>
          </div>
        </Card>
      )}

      {/* Filters */}
      <Card className="p-4">
        <div className="flex flex-col md:flex-row gap-4">
          <div className="flex-1">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
              <Input
                type="text"
                placeholder={t('incentives.search_milestones', 'Search milestones...')}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
              />
            </div>
          </div>
          <div className="w-full md:w-64">
            <Select value={filterType} onValueChange={setFilterType}>
              <SelectTrigger>
                <SelectValue placeholder={t('incentives.filter_by_type', 'Filter by type')} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">{t('incentives.all_types', 'All Types')}</SelectItem>
                {ACTIVITY_TYPES.map((type) => (
                  <SelectItem key={type} value={type}>
                    {type.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <Button variant="outline" onClick={fetchMilestones} className="flex items-center gap-2">
            <RefreshCw className="w-4 h-4" />
            {t('incentives.refresh', 'Refresh')}
          </Button>
        </div>
      </Card>

      {/* Milestones List */}
      <div className="space-y-6">
        {Object.keys(groupedMilestones).length === 0 ? (
          <Card className="p-12 text-center">
            <Target className="w-12 h-12 text-gray-300 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-gray-900 mb-2">{t('incentives.no_milestones_found', 'No Milestones Found')}</h3>
            <p className="text-gray-600 mb-4">
              {searchTerm || filterType !== 'all'
                ? 'Try adjusting your search or filters'
                : 'Create your first milestone to get started'}
            </p>
          </Card>
        ) : (
          Object.entries(groupedMilestones).map(([type, typeMilestones]) => (
            <div key={type}>
              <h2 className="text-xl font-semibold text-gray-900 mb-3 flex items-center gap-2">
                <Target className="w-5 h-5 text-blue-600" />
                {type.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                <Badge variant="outline" className="ml-2">
                  {typeMilestones.length}
                </Badge>
              </h2>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {typeMilestones.map((milestone) => (
                  <Card key={milestone.id} className="p-5 hover:shadow-md transition-shadow">
                    <div className="flex items-start justify-between mb-3">
                      <div className="flex-1">
                        <h3 className="text-lg font-semibold text-gray-900 mb-1">
                          {milestone.milestone_name}
                        </h3>
                        <p className="text-sm text-gray-600 mb-2">
                          {milestone.milestone_description || 'No description provided'}
                        </p>
                      </div>
                      <Badge className={milestone.is_active ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'}>
                        {milestone.is_active ? 'Active' : 'Inactive'}
                      </Badge>
                    </div>

                    <div className="grid grid-cols-2 gap-3 mb-4">
                      <div className="flex items-center gap-2 text-sm">
                        <Award className="w-4 h-4 text-purple-600" />
                        <span className="text-gray-700">{milestone.points_awarded} points</span>
                      </div>
                      <div className="flex items-center gap-2 text-sm">
                        <DollarSign className="w-4 h-4 text-green-600" />
                        <span className="text-gray-700">${milestone.monetary_value.toFixed(2)}</span>
                      </div>
                      <div className="col-span-2">
                        <Badge variant="outline" className="text-xs">
                          {milestone.is_repeatable ? 'Repeatable' : 'One-time'}
                        </Badge>
                      </div>
                    </div>

                    <div className="flex justify-end gap-2 pt-3 border-t border-gray-200">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleEdit(milestone)}
                        className="flex items-center gap-1"
                      >
                        <Edit className="w-3 h-3" />
                        {t('incentives.edit', 'Edit')}
                      </Button>
                      {milestone.is_active && (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleDeactivate(milestone.id)}
                          className="flex items-center gap-1 text-red-600 hover:text-red-700"
                        >
                          <Trash2 className="w-3 h-3" />
                          {t('incentives.deactivate', 'Deactivate')}
                        </Button>
                      )}
                    </div>
                  </Card>
                ))}
              </div>
            </div>
          ))
        )}
      </div>

      {/* Stats Summary */}
      <Card className="p-6 bg-blue-50 border-blue-200">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 text-center">
          <div>
            <div className="text-3xl font-bold text-blue-900">{milestones.length}</div>
            <div className="text-sm text-blue-700">{t('incentives.total_milestones', 'Total Milestones')}</div>
          </div>
          <div>
            <div className="text-3xl font-bold text-green-900">
              {milestones.filter(m => m.is_active).length}
            </div>
            <div className="text-sm text-green-700">{t('incentives.active', 'Active')}</div>
          </div>
          <div>
            <div className="text-3xl font-bold text-purple-900">
              {milestones.filter(m => m.is_repeatable).length}
            </div>
            <div className="text-sm text-purple-700">{t('incentives.repeatable', 'Repeatable')}</div>
          </div>
          <div>
            <div className="text-3xl font-bold text-orange-900">
              {Object.keys(groupedMilestones).length}
            </div>
            <div className="text-sm text-orange-700">{t('incentives.activity_types', 'Activity Types')}</div>
          </div>
        </div>
      </Card>
    </div>
  );
};

export default MilestoneManagement;
